'use client'

import clsx from 'clsx'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'

import { useHeaderLinks, useHeaderOrderOnlineNavigateClick, type ILink } from '../../_hooks'
import { matchesHeaderNavSubtreeActive } from '../../_utils/matchesHeaderNavSubtreeActive'
import { useActiveLink } from '../../_utils/useActiveLink'

import { AHeaderHamburgerNavBranch } from './a-header-hamburger-nav-branch'

interface AHeaderHamburgerServicesProps {
	onLinkClick: () => void
	menuOpen: boolean
}

export const AHeaderHamburgerServices = ({
	onLinkClick,
	menuOpen
}: AHeaderHamburgerServicesProps) => {
	const t = useTranslations('layout.header.hamburger.services')
	const tHamburgerLinks = useTranslations('layout.header.hamburger.links')
	const { secondaryLinks, mainLinks } = useHeaderLinks()
	const onOrderOnlineClick = useHeaderOrderOnlineNavigateClick(onLinkClick)
	const { pathWithoutLocale } = useActiveLink()
	const [openBranches, setOpenBranches] = useState<Set<string>>(() => new Set())
	const [careerPulseKey, setCareerPulseKey] = useState(0)

	useEffect(() => {
		if (!menuOpen) {
			setOpenBranches(new Set())
		}
	}, [menuOpen])

	useEffect(() => {
		if (!menuOpen) {
			return
		}
		const FIRST_PULSE_DELAY_MS = 500
		const PULSE_INTERVAL_MS = 5000

		let intervalId: number | undefined
		const firstTimeoutId = window.setTimeout(() => {
			setCareerPulseKey(k => k + 1)
			intervalId = window.setInterval(() => {
				setCareerPulseKey(k => k + 1)
			}, PULSE_INTERVAL_MS)
		}, FIRST_PULSE_DELAY_MS)

		return () => {
			window.clearTimeout(firstTimeoutId)
			if (intervalId !== undefined) {
				window.clearInterval(intervalId)
			}
		}
	}, [menuOpen])

	const toggleBranch = useCallback((key: string) => {
		setOpenBranches(prev => {
			const next = new Set(prev)
			if (next.has(key)) {
				next.delete(key)
			} else {
				next.add(key)
			}
			return next
		})
	}, [])

	const ambulances = secondaryLinks.find(l => l.to === '/ambulancie')
	const annualPrograms = secondaryLinks.find(
		l => l.to === '/rocne-programy' && l.children?.length
	)
	const preventive = secondaryLinks.find(l => l.to === '/preventivne-prehliadky')
	const medanteCredit = secondaryLinks.find(l => l.to === '/medante-kredit')
	const vipCare = secondaryLinks.find(l => l.to === '/rocne-programy/medante-platinum')

	const discounts = mainLinks.find(l => l.to === '/akciova-ponuka')
	const career = mainLinks.find(l => l.to === '/kariera' && l.children?.length)
	const team = mainLinks.find(l => l.to === '/tim')
	const blog = mainLinks.find(l => l.to === '/clanky')
	const forCompanies = mainLinks.find(l => l.to === '/pre-firmy')
	const contact = mainLinks.find(l => l.to === '/kontakt')

	const references: ILink = {
		label: tHamburgerLinks('references'),
		to: '/povedali-o-nas',
		prefetch: false
	}

	// const faq: ILink = {
	// 	label: tHamburgerLinks('faq'),
	// 	to: '/casto-kladene-otazky',
	// 	prefetch: false
	// }

	const renderSimpleItem = (link: ILink) => (
		<li
			key={`${link.to}-${link.label}`}
			className={clsx(matchesHeaderNavSubtreeActive(pathWithoutLocale, link) && 'active')}
		>
			<Link href={link.to} onClick={onLinkClick} prefetch={link.prefetch ?? false}>
				{link.label}
			</Link>
		</li>
	)

	return (
		<div className='header-hamburger-services'>
			<p className='header-hamburger-services__label'>{t('label')}</p>

			<div className='header-hamburger-services__columns'>
				<ul className='header-hamburger-services__column'>
					{ambulances ? renderSimpleItem(ambulances) : null}
					{annualPrograms ? (
						<AHeaderHamburgerNavBranch
							link={annualPrograms}
							branchKey='branch-annual'
							onLinkClick={onLinkClick}
							pathWithoutLocale={pathWithoutLocale}
							openBranches={openBranches}
							toggleBranch={toggleBranch}
						/>
					) : null}
					{preventive ? (
						<AHeaderHamburgerNavBranch
							link={preventive}
							branchKey='branch-preventive'
							onLinkClick={onLinkClick}
							pathWithoutLocale={pathWithoutLocale}
							openBranches={openBranches}
							toggleBranch={toggleBranch}
						/>
					) : null}
					{discounts ? renderSimpleItem(discounts) : null}
					{medanteCredit ? renderSimpleItem(medanteCredit) : null}
				</ul>

				<div className='header-hamburger-services__column-divider' aria-hidden />

				<ul className='header-hamburger-services__column'>
					{career ? (
						<AHeaderHamburgerNavBranch
							link={career}
							branchKey='branch-career'
							onLinkClick={onLinkClick}
							pathWithoutLocale={pathWithoutLocale}
							openBranches={openBranches}
							toggleBranch={toggleBranch}
							labelButtonClassName='header-hamburger-services__career-pulse'
							labelButtonKey={careerPulseKey}
						/>
					) : null}
					{team ? renderSimpleItem(team) : null}
					{blog ? renderSimpleItem(blog) : null}
					{renderSimpleItem(references)}
					{forCompanies ? renderSimpleItem(forCompanies) : null}
					{vipCare ? renderSimpleItem(vipCare) : null}
					{contact ? renderSimpleItem(contact) : null}
				</ul>
			</div>

			<div className='header-hamburger-services__divider' />

			<div className='header-hamburger-services__contact'>
				<div className='header-hamburger-services__contact-centrum'>
					<span className='label'>{t('contact.label')}</span>
					<a href={t('contact.href')} className='phone-number'>
						{t('contact.phone')}
					</a>
				</div>

				<Link
					href='/volne-terminy'
					title={t('order.button')}
					className='btn_basic type4'
					onClick={onOrderOnlineClick}
					prefetch={true}
				>
					<span className='label'>{t('order.button')}</span>

					<span className='icon'>
						<span>
							<svg className='sico'>
								<use href='/assets/layout/icons.svg#s_ar3_r' />
							</svg>
						</span>
						<span>
							<svg className='sico'>
								<use href='/assets/layout/icons.svg#s_ar3_r' />
							</svg>
						</span>
					</span>
				</Link>
			</div>
		</div>
	)
}
